'use client';

import React, { useRef, useCallback } from 'react';

const LENGTH = 6;

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  'aria-label'?: string;
  /** dark = fundo escuro (auth), light = fundo claro (perfil/configurações) */
  variant?: 'dark' | 'light';
}

const boxClassByVariant = {
  dark: `
    w-10 h-12 sm:w-12 sm:h-14 rounded-lg text-center text-lg sm:text-xl font-semibold
    bg-white/10 border border-white/20 text-white
    focus:outline-none focus:border-cyan focus:ring-2 focus:ring-cyan
    transition-colors duration-200
    disabled:opacity-50 disabled:cursor-not-allowed
  `.trim(),
  light: `
    w-10 h-12 sm:w-12 sm:h-14 rounded-lg text-center text-lg sm:text-xl font-semibold
    bg-white border border-border-light text-navy-dark
    focus:outline-none focus:border-cyan focus:ring-2 focus:ring-cyan
    transition-colors duration-200
    disabled:opacity-50 disabled:cursor-not-allowed
  `.trim(),
};

export const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  label,
  error,
  disabled = false,
  'aria-label': ariaLabel = 'Código de verificação',
  variant = 'dark',
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const digits = value.split('').concat(Array(LENGTH).fill('')).slice(0, LENGTH);

  const setDigit = useCallback(
    (index: number, digit: string) => {
      if (!/^\d*$/.test(digit)) return;
      const next = value.split('');
      next[index] = digit.slice(-1);
      const newValue = next.join('').slice(0, LENGTH);
      onChange(newValue);
      if (digit && index < LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [value, onChange]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        const next = value.split('');
        next[index - 1] = '';
        onChange(next.join(''));
      }
    },
    [value, digits, onChange]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH);
      if (pasted) {
        onChange(pasted);
        const nextIndex = Math.min(pasted.length, LENGTH - 1);
        inputRefs.current[nextIndex]?.focus();
      }
    },
    [onChange]
  );

  const boxClass = `${boxClassByVariant[variant]} ${error ? 'border-red-400 focus:border-red-400 focus:ring-red-400' : ''}`;
  const labelClass = variant === 'dark' ? 'text-white/90' : 'text-navy-dark';
  const errorClass = variant === 'dark' ? 'text-red-300' : 'text-red-600';

  return (
    <div>
      {label && (
        <label className={`block text-sm font-medium mb-1.5 ${labelClass}`}>
          {label}
        </label>
      )}
      <div
        className="flex justify-center gap-2 sm:gap-3"
        role="group"
        aria-label={ariaLabel}
        onPaste={handlePaste}
      >
        {Array.from({ length: LENGTH }, (_, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digits[i] ?? ''}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={disabled}
            className={boxClass}
            aria-label={`Dígito ${i + 1} de ${LENGTH}`}
          />
        ))}
      </div>
      {error && (
        <p className={`mt-1.5 text-sm ${errorClass}`} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
